import { encryptForUser, encryptForUserWithFallback, decryptForUser } from "@/lib/encryption";
import { securityService } from "./securityService";
import { maskPanNumber } from "@/utils/panStorage";

const ENCRYPTION_SETUP_MESSAGE =
  "Unable to set up encryption for your account. Please sign out and sign back in, then try again.";

export class PanEncryptionSetupError extends Error {
  constructor(message: string = ENCRYPTION_SETUP_MESSAGE) {
    super(message);
    this.name = "PanEncryptionSetupError";
  }
}

/**
 * Enhanced encryption service for sensitive financial data
 * Provides secure encryption/decryption with audit logging
 */
export class EncryptionService {
  private static instance: EncryptionService;

  public static getInstance(): EncryptionService {
    if (!EncryptionService.instance) {
      EncryptionService.instance = new EncryptionService();
    }
    return EncryptionService.instance;
  }

  /**
   * Encrypt PAN data for secure storage
   */
  async encryptPANData(
    userId: string,
    panNumber: string
  ): Promise<{
    encrypted_pan_number: string;
    pan_encryption_iv: string;
    masked_pan: string;
  }> {
    try {
      // Validate PAN format before encryption
      const validation = securityService.validatePANNumber(panNumber);
      if (!validation.isValid) {
        throw new Error(`PAN validation failed: ${validation.error}`);
      }

      // Sanitize PAN number
      const cleanPAN = securityService.sanitizeInput(panNumber).replace(/\s+/g, "").toUpperCase();

      // Encrypt PAN using user-specific encryption (fallback to plain if key provisioning fails)
      const encryptionResult = await encryptForUserWithFallback(cleanPAN, userId);

      if (!encryptionResult.encrypted) {
        throw new PanEncryptionSetupError();
      }

      // Create masked version (last 4 digits of PAN number)
      const maskedPAN = maskPanNumber(cleanPAN);

      // Log encryption event
      await securityService.logSecurityEvent("PAN_ENCRYPT", "family_member", "new", "encrypt_pan", {
        masked_pan: maskedPAN,
      });

      return {
        encrypted_pan_number: encryptionResult.cipher,
        pan_encryption_iv: encryptionResult.iv || "",
        masked_pan: maskedPAN,
      };
    } catch (error) {
      const isSetupFailure = error instanceof PanEncryptionSetupError;
      console.warn("PAN encryption failed:", error);

      if (!isSetupFailure) {
        await securityService.logSecurityEvent(
          "PAN_ENCRYPT_ERROR",
          "family_member",
          "new",
          "encrypt_pan_failed",
          { error: error instanceof Error ? error.message : "Unknown error" }
        );
      }

      if (isSetupFailure) {
        throw error;
      }

      throw new Error("Failed to encrypt PAN data");
    }
  }

  /**
   * Decrypt PAN data for authorized access
   */
  async decryptPANData(
    userId: string,
    encryptedPAN: string,
    iv: string,
    memberId: string
  ): Promise<string> {
    try {
      // Log decryption access
      await securityService.logSecurityEvent(
        "PAN_DECRYPT",
        "family_member",
        memberId,
        "decrypt_pan_access"
      );

      // Decrypt PAN using user-specific decryption
      const decryptedPAN = await decryptForUser(encryptedPAN, iv, userId);

      if (!decryptedPAN) {
        throw new Error("Decryption returned empty result");
      }

      // Validate decrypted PAN format
      const validation = securityService.validatePANNumber(decryptedPAN);
      if (!validation.isValid) {
        throw new Error("Decrypted PAN failed validation");
      }

      return decryptedPAN;
    } catch (error) {
      console.error("PAN decryption failed:", error);

      await securityService.logSecurityEvent(
        "PAN_DECRYPT_ERROR",
        "family_member",
        memberId,
        "decrypt_pan_failed",
        { error: error instanceof Error ? error.message : "Unknown error" }
      );

      throw new Error("Failed to decrypt PAN data");
    }
  }

  /**
   * Encrypt general sensitive data
   */
  async encryptSensitiveData(
    userId: string,
    data: string,
    dataType: string
  ): Promise<{
    encrypted_data: string;
    encryption_iv: string;
  }> {
    try {
      // Sanitize input data
      const cleanData = securityService.sanitizeInput(data);

      // Encrypt data
      const encryptionResult = await encryptForUser(cleanData, userId);

      // Log encryption event
      await securityService.logSecurityEvent("DATA_ENCRYPT", dataType, "new", "encrypt_data");

      return {
        encrypted_data: encryptionResult.cipher,
        encryption_iv: encryptionResult.iv,
      };
    } catch (error) {
      console.error("Data encryption failed:", error);
      throw new Error("Failed to encrypt sensitive data");
    }
  }

  /**
   * Decrypt general sensitive data
   */
  async decryptSensitiveData(
    userId: string,
    encryptedData: string,
    iv: string,
    dataType: string,
    resourceId: string
  ): Promise<string> {
    try {
      // Log decryption access
      await securityService.logSecurityEvent(
        "DATA_DECRYPT",
        dataType,
        resourceId,
        "decrypt_data_access"
      );

      // Decrypt data
      const decryptedData = await decryptForUser(encryptedData, iv, userId);

      if (!decryptedData) {
        throw new Error("Decryption returned empty result");
      }

      return decryptedData;
    } catch (error) {
      console.error("Data decryption failed:", error);

      await securityService.logSecurityEvent(
        "DATA_DECRYPT_ERROR",
        dataType,
        resourceId,
        "decrypt_data_failed",
        { error: error instanceof Error ? error.message : "Unknown error" }
      );

      throw new Error("Failed to decrypt sensitive data");
    }
  }

  /**
   * Decrypt asset data for authorized access
   */
  async decryptAssetData(
    userId: string,
    encryptedData: string,
    iv: string,
    assetId: string
  ): Promise<string> {
    const result = await this.decryptSensitiveData(userId, encryptedData, iv, "asset", assetId);
    return result;
  }

  /**
   * Encrypt asset data for secure storage
   */
  async encryptAssetData(
    userId: string,
    data: string
  ): Promise<{
    encrypted_data: string;
    encryption_iv: string;
  }> {
    const result = await this.encryptSensitiveData(userId, data, "asset");
    return result;
  }

  /**
   * Decrypt profile data for authorized access
   */
  async decryptProfileData(
    userId: string,
    encryptedData: string,
    iv: string,
    fieldName: string
  ): Promise<string> {
    const result = await this.decryptSensitiveData(userId, encryptedData, iv, "profile", fieldName);
    return result;
  }

  /**
   * Encrypt profile data for secure storage
   */
  async encryptProfileData(
    userId: string,
    data: string
  ): Promise<{
    encrypted_data: string;
    encryption_iv: string;
  }> {
    const result = await this.encryptSensitiveData(userId, data, "profile");
    return result;
  }
}

export const encryptionService = EncryptionService.getInstance();
